/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import * as Sentry from "@sentry/react";
import LoadingScreen from "components/LoadingScreen";
import ErrorBoundaryPage from "pages/FallbackPages/ErrorBoundary";
import { Suspense, useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import toast from "react-hot-toast";
import { useLocation } from "react-router-dom";
import { useAuth } from "state/AuthContext";
import { useUser } from "state/UserContext";
import { sentryCaptureError } from "utils/errorFormatter";
import warning from "utils/warning";

export const RouteManager = ({ children }) => {
  const { isLoggedIn, isAuthLoading } = useAuth();
  const { userData } = useUser();

  useEffect(() => {
    if (isLoggedIn && userData) {
      Sentry.setUser({
        id: userData.id,
        hash_id: userData.hash_id,
        email: userData.email,
      });
    } else {
      Sentry.setUser(null); // clear user on logout
    }
  }, [isLoggedIn, userData]);

  if (isAuthLoading) {
    return <LoadingScreen />;
  }

  //  if logged in, show the route
  return (
    <>
      <SafeComponent>{isLoggedIn && children}</SafeComponent>
    </>
  );
};

export default RouteManager;

// the alerts is used to prevent the error boundary from executing multiple times
let alerts = 0;

export const SafeComponent = ({ children }) => {
  const { pathname } = useLocation();
  return (
    <ErrorBoundary
      FallbackComponent={({ error }) => {
        const status = error.statusCode || error.status;
        if (status && (status === 403 || status === 404)) {
          return null;
        }
        return <ErrorBoundaryPage />;
      }}
      onError={(error, info) => {
        if (alerts > 0) return;
        // Use the new formatted error capture
        sentryCaptureError(error, `React Error Boundary - ${pathname}`);

        warning({
          isCustom: true,
          error: error.message,
          stack: info.componentStack,
        });
        toast.error(
          "An error happened. Please report the incident to the support.",
        );
        return (alerts += 1);
      }}
      resetKeys={[pathname]}
      onResetKeysChange={() => {
        alerts = 0;
      }}
    >
      <Suspense fallback={<LoadingScreen />}>{children}</Suspense>
    </ErrorBoundary>
  );
};
